/* eslint-disable react/jsx-no-useless-fragment */
import React, { Fragment, useEffect, useState } from "react";

import { useLazyQuery } from "@apollo/client";
import { Col, Row } from "reactstrap";

import TEMPLATE_WITH_SECTIONS from "graphql/sanity/templateWithSections";

import CustomHelmet from "components/calculations/components/CustomHelmet";
import Button from "components/common/Button";
import Loading from "components/common/Loading";
import Modal from "components/common/Modal";
import { DocumentPreview } from "components/documents/DocumentPreview";
import PrecedentsTree from "components/precedentsTree/PrecedentsTree";

import useModal from "hooks/useModal";

import { extractSectionHtmlById } from "helpers/extractSectionHtmlById";
import { titleCase } from "helpers/titleCase";
import { truncateText } from "helpers/truncateText";

export const DOCUMENT_SECTIONS_MODAL_NAME = "CLIENT_DOCUMENT_SECTIONS";

const convertTreeToNestedFormat = (tree) => {
  function processNodes(nodes) {
    if (!nodes) return [];

    return nodes.flatMap((node) => [
      { section: node.id, hidden: !node.checked },
      ...processNodes(node.nodes),
    ]);
  }

  return processNodes(tree.nodes);
};

const generateTree = (args) => {
  const { sections = [], config = {} } = args;

  const defaultCheckedFields = config.defaultSectionsState || {};

  const sortedSections = [...sections].sort((a, b) => {
    const aParts = a.section.split(".").map((part) => parseInt(part, 10) || 0);
    const bParts = b.section.split(".").map((part) => parseInt(part, 10) || 0);

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aPart = aParts[i] || 0;
      const bPart = bParts[i] || 0;

      if (aPart !== bPart) {
        return aPart - bPart;
      }
    }
    return 0;
  });

  const parseSectionName = (bookmarkSection) => {
    const { label, section, tag, hidden } = bookmarkSection;

    const numbers = section.split(".").map((s) => Number.parseInt(s, 10));

    return {
      section,
      label,
      numbers,
      tag,
      hidden,
    };
  };

  const parsedSections = sortedSections.map((bn) => parseSectionName(bn));

  const genLevelNodes = (level, current) => {
    const filteredSections = parsedSections.filter(
      (b) => b.numbers.length === level + 1 && current.every((num, idx) => b.numbers[idx] === num),
    );

    if (filteredSections.length === 0) return null;

    const isRoot = level === 0;

    const nodes = filteredSections.map((sectionInfo) => {
      const { label, numbers, section, hidden } = sectionInfo;

      const childNodes = genLevelNodes(level + 1, numbers);

      const title = (() => {
        const isNumeralSection = section.replace(/[0-9.]/g, "").length === 0;

        if (isNumeralSection) {
          return `${section}${isRoot ? "." : ""} ${titleCase(label)}`;
        }

        return `${titleCase(label)}`;
      })();

      const node = {
        title,
        checked: Object.prototype.hasOwnProperty.call(defaultCheckedFields, label)
          ? defaultCheckedFields[label]
          : !hidden,
        key: label,
        id: section,
        nestingLevel: level,
        nodes: childNodes,
        className: isRoot ? "parent-node" : undefined,
      };

      return node;
    });

    return nodes;
  };

  const nodes = genLevelNodes(0, []);

  return {
    nodes,
  };
};

const ClientDocumentsSectionsModal = () => {
  const { options, close } = useModal(DOCUMENT_SECTIONS_MODAL_NAME);

  const { document, sections, submit: createDocument, client } = options || {};
  const [tree, setTree] = useState([]);
  const [node, setNode] = useState(null);
  const [html, setHtml] = useState("");
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  const [getDescriptionById] = useLazyQuery(TEMPLATE_WITH_SECTIONS, {
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (sections) {
      setTree(
        generateTree({
          sections: sections || [],
          config: {
            defaultSectionsState: {
              "Remote Signing": !!client?.remoteSigningEnabled,
            },
          },
        }),
      );
      setNode(sections[0]);
    }
  }, [sections, client?.remoteSigningEnabled]);

  useEffect(() => {
    const fetchTemplateDescription = async () => {
      if (document?._id) {
        setIsLoadingTemplate(true);
        try {
          const { data } = await getDescriptionById({
            variables: { id: document._id },
          });

          const sectionDescription = data?.allDocumentTemplate?.[0]?.description || "";
          setHtml(sectionDescription);
        } catch (error) {
          console.error("[ERROR LOADING TEMPLATE DESCRIPTION]:", error);
          setHtml("");
        } finally {
          setIsLoadingTemplate(false);
        }
      }
    };

    fetchTemplateDescription();
  }, [document?._id, getDescriptionById]);

  const onChange = async (key, val) => {
    const parseKeyToNumbers = (key) => key.split(".").map((s) => Number.parseInt(s, 10));

    const keyNumbers = parseKeyToNumbers(key);

    const setNode = (node) => {
      const nodeNumbers = parseKeyToNumbers(node.id || "");

      const isNested = keyNumbers.every((num, idx) => nodeNumbers[idx] === num);

      const updatedNode = {
        ...node,
        checked: isNested ? val : node.checked,
        nodes: node.nodes?.map((n) => setNode(n)),
      };

      if (!isNested && node.nodes) {
        const isAnyChildChecked = node.nodes.some((n) => n.checked);
        updatedNode.checked = isAnyChildChecked;
      }

      return updatedNode;
    };

    const updateParentNodeState = (node) => {
      if (node.nodes) {
        const updatedNodes = node.nodes.map(updateParentNodeState);
        const isAnyChildChecked = updatedNodes.some((n) => n.checked);
        if (isAnyChildChecked) {
          node.checked = true;
        }
        node.nodes = updatedNodes;
      }
      return node;
    };

    const updatedTree = setNode(tree);

    setTree(updateParentNodeState(updatedTree));
  };

  const handleSubmit = () => {
    const sections = convertTreeToNestedFormat(tree);

    createDocument(sections);

    close();
  };

  const handleSetNode = (id) => {
    if (node.section === id) return null;

    setNode(sections.find((section) => section.section === id));
  };

  return (
    <Modal
      size="md"
      name={DOCUMENT_SECTIONS_MODAL_NAME}
      title="Select Sections"
      noFooter
      showCloseButton
      className="client-document-sections-form"
    >
      <CustomHelmet
        title="Document Sections"
        description="Document Sections"
        href="https://www.divorcepath.com/app/[new-document]"
        isCanonical
      />
      <Fragment>
        <Row>
          <Col sm={5}>
            <div className="sections-modal-content left">
              <PrecedentsTree
                tree={tree}
                selectedNode={node}
                onCheckboxChange={onChange}
                onNodeSelect={handleSetNode}
              />
            </div>
          </Col>
          <Col sm={7}>
            <div className="sections-modal-content right">
              <div className="section-description-content">
                <div className="section-title">
                  {node?.section &&
                    `${node?.section}${node?.section?.includes(".") ? "" : "."} ${node?.label}`}
                </div>

                {isLoadingTemplate ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "300px",
                      width: "100%",
                    }}
                  >
                    <Loading />
                  </div>
                ) : (
                  <DocumentPreview
                    html={truncateText(extractSectionHtmlById(html, node?.section))}
                  />
                )}
              </div>
            </div>
          </Col>
          <div className="sections-modal-footer">
            <div style={{ marginLeft: "20px" }}>
              <strong>* Check schedules to include</strong>
            </div>
            <div className="sections-modal-footer-buttons">
              <Button size="sm" onClick={handleSubmit} className="btn-submit">
                Confirm
              </Button>
              <Button size="sm" color="secondary" onClick={close} className="btn-cancel">
                Cancel
              </Button>
            </div>
          </div>
        </Row>
      </Fragment>
    </Modal>
  );
};

export default ClientDocumentsSectionsModal;
